import { useCallback, useEffect, useState } from "react";
import { LuHandCoins } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioFormaPagamento } from "../../sync/repositories/financeiro";

const AJUDA = {
  titulo: "Cadastro de Formas de Pagamento",
  descricao:
    "Cadastre aqui as formas de pagamento/recebimento usadas na fazenda (ex.: Dinheiro, Boleto, Cartão). Ao selecionar uma forma de pagamento numa Conta a Pagar ou a Receber, os valores padrão de juros, multa e parcelamento definidos aqui são preenchidos automaticamente.",
  secoes: [
    {
      titulo: "Informações Básicas",
      campos: [
        {
          nome: "Nome",
          obrigatorio: true,
          explicacao: "Nome da forma de pagamento (ex.: 'Dinheiro', 'Cartão de Crédito', 'Boleto').",
        },
        {
          nome: "Tipo",
          obrigatorio: true,
          explicacao: "'À vista' para pagamento integral no ato, ou 'A prazo' para pagamento parcelado ou com prazo — o tipo 'A prazo' libera as opções de parcelamento abaixo.",
        },
        {
          nome: "Status (Ativo)",
          obrigatorio: false,
          explicacao: "Só formas marcadas como ativas aparecem como opção ao criar uma nova conta.",
        },
      ],
    },
    {
      titulo: "Parcelamento",
      campos: [
        {
          nome: "Permite Parcelamento",
          obrigatorio: false,
          explicacao: "Habilita o parcelamento para essa forma de pagamento — só aparece quando o Tipo é 'A prazo'.",
        },
        {
          nome: "Máximo de Parcelas / Intervalo entre Parcelas",
          obrigatorio: false,
          explicacao: "Quantas parcelas essa forma permite no máximo, e o intervalo em dias entre uma parcela e outra (ex.: 30 dias = parcelas mensais).",
        },
      ],
    },
    {
      titulo: "Taxas e Juros",
      campos: [
        {
          nome: "Taxa da Operação",
          obrigatorio: false,
          explicacao: "Percentual cobrado por transação usando essa forma de pagamento (ex.: taxa de cartão).",
        },
        {
          nome: "Multa por Atraso / Juros por Atraso",
          obrigatorio: false,
          explicacao: "Percentuais aplicados automaticamente quando uma conta com essa forma de pagamento vence sem ser paga.",
        },
      ],
    },
    {
      titulo: "Prazos e Configurações",
      campos: [
        {
          nome: "Prazo Padrão (dias)",
          obrigatorio: false,
          explicacao: "Quantidade padrão de dias até o vencimento, usada para sugerir a data de vencimento ao criar uma conta com essa forma de pagamento.",
        },
        {
          nome: "Tipo de Recebimento",
          obrigatorio: false,
          explicacao: "Quando o valor efetivamente cai na conta (Imediato, D+30, D+60, D+90) — relevante para formas como cartão, em que o recebimento não é instantâneo.",
        },
        {
          nome: "Exige Comprovante",
          obrigatorio: false,
          explicacao: "Marque se o pagamento com essa forma precisa de comprovante anexado.",
        },
      ],
    },
  ],
  dicas: [
    "Os valores de juros, multa e parcelamento cadastrados aqui são apenas um ponto de partida — ao criar uma Conta a Pagar ou a Receber, ainda é possível ajustar esses valores manualmente para aquele lançamento específico.",
  ],
};

export default function GestaoFormasPagamento() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [formas, setFormas] = useState([]);
  const [formasFiltradas, setFormasFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    busca: "",
    tipo: ""
  });
  
  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);
  
  const [formData, setFormData] = useState({
    nome: "",
    tipo: "avista",
    ativo: true,
    permite_parcelamento: false,
    max_parcelas: "",
    taxa: "",
    multa_atraso: "",
    juros_atraso: "",
    prazo_dias: "",
    intervalo_parcelas: "",
    tipo_recebimento: "",
    exige_comprovante: false,
    descricao: "",
  });

  // Tipos de forma de pagamento
  const tiposForma = [
    { value: "avista", label: "À vista", descricao: "Pagamento integral no ato" },
    { value: "aprazo", label: "A prazo", descricao: "Pagamento parcelado ou com prazo" },
  ];

  // Tipos de recebimento
  const tiposRecebimento = [
    { value: "", label: "Não informado" },
    { value: "imediato", label: "Imediato" },
    { value: "d30", label: "D+30" },
    { value: "d60", label: "D+60" },
    { value: "d90", label: "D+90" },
  ];

  // Carregar formas de pagamento
  const carregarFormas = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioFormaPagamento.listar();
      const dados = response.data.results || response.data;
      setFormas(Array.isArray(dados) ? dados : []);
      setFormasFiltradas(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar formas de pagamento:", error);
      alert("Erro ao carregar lista de formas de pagamento");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarFormas();
  }, [carregarFormas]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validarFormulario = () => {
    if (!formData.nome.trim()) {
      alert("Informe o nome da forma de pagamento.");
      return false;
    }
    if (formData.permite_parcelamento && (!formData.max_parcelas || Number(formData.max_parcelas) <= 0)) {
      alert("Informe o número máximo de parcelas.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      nome: formData.nome,
      tipo: formData.tipo,
      ativo: formData.ativo,
      permite_parcelamento: formData.permite_parcelamento,
      max_parcelas: formData.permite_parcelamento && formData.max_parcelas ? Number(formData.max_parcelas) : null,
      intervalo_parcelas: formData.permite_parcelamento && formData.intervalo_parcelas ? Number(formData.intervalo_parcelas) : null,
      taxa: formData.taxa ? Number(formData.taxa) : null,
      multa_atraso: formData.multa_atraso ? Number(formData.multa_atraso) : null,
      juros_atraso: formData.juros_atraso ? Number(formData.juros_atraso) : null,
      prazo_dias: formData.prazo_dias ? Number(formData.prazo_dias) : null,
      tipo_recebimento: formData.tipo_recebimento || null,
      exige_comprovante: formData.exige_comprovante,
      descricao: formData.descricao || "",
    };

    try {
      if (editando) {
        await repositorioFormaPagamento.atualizar(editandoId, payload);
        alert("✅ Forma de pagamento atualizada com sucesso!");
        handleCancel();
        carregarFormas();
      } else {
        await repositorioFormaPagamento.criar(payload);
        alert("✅ Forma de pagamento cadastrada com sucesso!");
        handleCancel();
        carregarFormas();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (forma) => {
    setFormData({
      nome: forma.nome || "",
      tipo: forma.tipo || "avista",
      ativo: forma.ativo === true || forma.ativo === "true",
      permite_parcelamento: forma.permite_parcelamento === true || forma.permite_parcelamento === "true",
      max_parcelas: forma.max_parcelas || "",
      taxa: forma.taxa || "",
      multa_atraso: forma.multa_atraso || "",
      juros_atraso: forma.juros_atraso || "",
      prazo_dias: forma.prazo_dias || "",
      intervalo_parcelas: forma.intervalo_parcelas || "",
      tipo_recebimento: forma.tipo_recebimento || "",
      exige_comprovante: forma.exige_comprovante === true || forma.exige_comprovante === "true",
      descricao: forma.descricao || "",
    });
    setEditando(true);
    setEditandoId(forma.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovo = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      tipo: "avista",
      ativo: true,
      permite_parcelamento: false,
      max_parcelas: "",
      taxa: "",
      multa_atraso: "",
      juros_atraso: "",
      prazo_dias: "",
      intervalo_parcelas: "",
      tipo_recebimento: "",
      exige_comprovante: false,
      descricao: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("⚠️ Tem certeza que deseja excluir esta forma de pagamento? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioFormaPagamento.excluir(id);
      alert("✅ Forma de pagamento excluída com sucesso!");
      carregarFormas();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("❌ Erro ao excluir forma de pagamento. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(formas) ? [...formas] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(f => 
        f.nome?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.tipo) {
      filtrados = filtrados.filter(f => f.tipo === filtros.tipo);
    }
    
    setFormasFiltradas(filtrados);
    setPaginaAtual(1);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", tipo: "" });
    setFormasFiltradas(formas);
    setPaginaAtual(1);
  };

  const getTipoLabel = (tipo) => {
    const tipoEncontrado = tiposForma.find(t => t.value === tipo);
    return tipoEncontrado ? tipoEncontrado.label : tipo;
  };

  const getTipoColor = (tipo) => {
    return tipo === "avista" 
      ? "bg-green-100 text-green-800" 
      : "bg-blue-100 text-blue-800";
  };

  const getRecebimentoLabel = (tipo) => {
    const tipoEncontrado = tiposRecebimento.find(t => t.value === tipo);
    return tipoEncontrado ? tipoEncontrado.label : "—";
  };

  const formatarPorcentagem = (valor) => {
    if (!valor && valor !== 0) return "—";
    return `${Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  };

  const formatarDias = (dias) => {
    if (!dias && dias !== 0) return "—";
    return `${dias} dias`;
  };

  const indiceUltimoItem = paginaAtual * itensPorPagina;
  const indicePrimeiroItem = indiceUltimoItem - itensPorPagina;
  const formasPaginadas = formasFiltradas.slice(indicePrimeiroItem, indiceUltimoItem);
  const totalPaginas = Math.ceil(formasFiltradas.length / itensPorPagina);

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Formas de Pagamento <LuHandCoins size ={38}/></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovo}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Forma de Pagamento
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Forma de Pagamento" : "Nova Forma de Pagamento"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Seção 1: Informações Básicas */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">Informações Básicas</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Nome *</label>
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Ex: Dinheiro, Cartão de Crédito, Boleto..."
                        required
                      />
                      <span className="text-xs text-gray-500 mt-1">Nome identificador da forma de pagamento</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                      <select
                        name="tipo"
                        value={formData.tipo}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione...</option>
                        {tiposForma.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-500 mt-1">
                        {tiposForma.find(t => t.value === formData.tipo)?.descricao}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Status</label>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="ativo"
                            checked={formData.ativo}
                            onChange={handleChange}
                            className="w-4 h-4 text-green-600"
                          />
                          <span className="text-gray-700">Ativo</span>
                        </label>
                        <span className="text-xs text-gray-500">
                          (Apenas formas ativas aparecem nas opções)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seção 2: Parcelamento (só aparece se tipo for "aprazo") */}
                {formData.tipo === "aprazo" && (
                  <div className="border-b pb-4">
                    <h4 className="text-lg font-semibold text-gray-700 mb-4">Parcelamento</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col">
                        <label className="flex items-center gap-2 cursor-pointer mb-2">
                          <input
                            type="checkbox"
                            name="permite_parcelamento"
                            checked={formData.permite_parcelamento}
                            onChange={handleChange}
                            className="w-4 h-4 text-green-600"
                          />
                          <span className="font-medium text-gray-700">Permite parcelamento</span>
                        </label>
                        <span className="text-xs text-gray-500 ml-6">Habilitar parcelamento para esta forma</span>
                      </div>

                      {formData.permite_parcelamento && (
                        <>
                          <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-1">Máximo de Parcelas</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                name="max_parcelas"
                                value={formData.max_parcelas}
                                onChange={handleChange}
                                className="w-32 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                                min="1"
                                max="24"
                              />
                              <span className="text-gray-500">x</span>
                            </div>
                            <span className="text-xs text-gray-500 mt-1">Número máximo de parcelas permitido</span>
                          </div>

                          <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-1">Intervalo entre Parcelas (dias)</label>
                            <input
                              type="number"
                              name="intervalo_parcelas"
                              value={formData.intervalo_parcelas}
                              onChange={handleChange}
                              className="w-32 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                              min="1"
                            />
                            <span className="text-xs text-gray-500 mt-1">Ex: 30 dias = parcelas mensais</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Seção 3: Taxas e Juros */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">Taxas e Juros</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Taxa da Operação</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input
                          type="number"
                          step="0.01"
                          name="taxa"
                          value={formData.taxa}
                          onChange={handleChange}
                          className="flex-1 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                          placeholder="0,00"
                        />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">%</span>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">Taxa cobrada por transação</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Multa por Atraso</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input
                          type="number"
                          step="0.01"
                          name="multa_atraso"
                          value={formData.multa_atraso}
                          onChange={handleChange}
                          className="flex-1 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                          placeholder="0,00"
                        />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">%</span>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">Percentual de multa por atraso</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Juros por Atraso</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input
                          type="number"
                          step="0.01"
                          name="juros_atraso"
                          value={formData.juros_atraso}
                          onChange={handleChange}
                          className="flex-1 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                          placeholder="0,00"
                        />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">%</span>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">Juros mensais por atraso</span>
                    </div>
                  </div>
                </div>

                {/* Seção 4: Prazos e Configurações */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">Prazos e Configurações</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Prazo Padrão (dias)</label>
                      <input
                        type="number"
                        name="prazo_dias"
                        value={formData.prazo_dias}
                        onChange={handleChange}
                        className="w-32 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        min="0"
                      />
                      <span className="text-xs text-gray-500 mt-1">Dias para vencimento padrão</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Recebimento</label>
                      <select
                        name="tipo_recebimento"
                        value={formData.tipo_recebimento}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      >
                        {tiposRecebimento.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-500 mt-1">Quando o valor é recebido</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="exige_comprovante"
                          checked={formData.exige_comprovante}
                          onChange={handleChange}
                          className="w-4 h-4 text-green-600"
                        />
                        <span className="font-medium text-gray-700">Exige comprovante</span>
                      </label>
                      <span className="text-xs text-gray-500 mt-1 ml-6">Necessário anexar comprovante</span>
                    </div>
                  </div>
                </div>

                {/* Seção 5: Descrição */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <textarea
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    rows={3}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Informações adicionais sobre esta forma de pagamento..."
                  />
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {/* Filtros e Tabela - visíveis apenas quando formulário está fechado */}
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome</label>
                  <input
                    type="text"
                    placeholder="Nome da forma de pagamento..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={filtros.tipo}
                    onChange={(e) => setFiltros({...filtros, tipo: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todos os tipos</option>
                    {tiposForma.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Formas de Pagamento */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center flex-wrap gap-2">
                <p className="text-gray-600">
                  {formasFiltradas.length} forma(s) de pagamento encontrada(s)
                </p>
                <div className="text-sm text-gray-500">
                  Página {paginaAtual} de {totalPaginas || 1}
                </div>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">ID</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Tipo</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Parcelamento</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Taxa</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Multa</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Juros</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Status</th>
                          <th className="p-3 text-center text-sm font-semibold text-gray-700">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formasPaginadas.map(forma => (
                          <tr key={forma.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                            <td className="p-3 text-sm font-mono">{forma.id}</td>
                            <td className="p-3 text-sm font-medium">{forma.nome}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTipoColor(forma.tipo)}`}>
                                {getTipoLabel(forma.tipo)}
                              </span>
                            </td>
                            <td className="p-3 text-sm text-center">
                              {forma.permite_parcelamento ? (
                                <span className="font-medium text-green-600">{forma.max_parcelas}x</span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="p-3 text-sm">{formatarPorcentagem(forma.taxa)}</td>
                            <td className="p-3 text-sm">{formatarPorcentagem(forma.multa_atraso)}</td>
                            <td className="p-3 text-sm">{formatarPorcentagem(forma.juros_atraso)}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${forma.ativo ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
                                {forma.ativo ? "Ativo" : "Inativo"}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() => handleEditar(forma)}
                                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Editar"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() => handleExcluir(forma.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Excluir"
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        
                        {formasPaginadas.length === 0 && (
                          <tr>
                            <td colSpan="9" className="p-8 text-center text-gray-500">
                              {filtros.busca || filtros.tipo
                                ? "Nenhuma forma de pagamento encontrada com os filtros selecionados."
                                : "Nenhuma forma de pagamento cadastrada."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  {totalPaginas > 1 && (
                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-center items-center gap-2">
                      <button
                        onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
                        disabled={paginaAtual === 1}
                        className={`px-3 py-1 rounded transition-colors ${paginaAtual === 1 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                      >
                        ◀ Anterior
                      </button>
                      <div className="flex gap-1">
                        {Array.from({ length: Math.min(totalPaginas, 5) }, (_, i) => {
                          let num;
                          if (totalPaginas <= 5) num = i + 1;
                          else if (paginaAtual <= 3) num = i + 1;
                          else if (paginaAtual >= totalPaginas - 2) num = totalPaginas - 4 + i;
                          else num = paginaAtual - 2 + i;
                          return (
                            <button
                              key={num}
                              onClick={() => setPaginaAtual(num)}
                              className={`w-8 h-8 rounded transition-colors ${paginaAtual === num ? "bg-green-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                            >
                              {num}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
                        disabled={paginaAtual === totalPaginas}
                        className={`px-3 py-1 rounded transition-colors ${paginaAtual === totalPaginas ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                      >
                        Próxima ▶
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}